/**
 * TemplateManager Component
 *
 * Allows managers to:
 * - View available settings templates
 * - Create new templates
 * - Apply templates to team members
 * - Delete templates
 *
 * Date: 2026-01-11
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Copy,
    Plus,
    Trash2,
    Check,
    X,
    AlertCircle,
    Tag,
    Award,
    ChevronRight
} from 'lucide-react';
import API_BASE_URL from '../apiConfig';

const TemplateManager = ({ currentUser, teamMembers, selectedMembers, onTemplateApplied }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [filterRole, setFilterRole] = useState('all');

    const loadTemplates = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = filterRole !== 'all' ? { target_role: filterRole } : {};
            const response = await axios.get(
                `${API_BASE_URL}/api/manager/templates`,
                { params }
            );

            setTemplates(response.data);
            setLoading(false);
        } catch (err) {
            console.error('Error loading templates:', err);
            setError(err.response?.data?.detail || 'Failed to load templates');
            setLoading(false);
        }
    }, [filterRole]);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    const handleApplyTemplate = (template) => {
        setSelectedTemplate(template);
        setShowApplyModal(true);
    };

    const handleDeleteTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template?')) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/api/manager/templates/${templateId}`);
            setSuccess('Template deleted successfully');
            setTimeout(() => setSuccess(null), 3000);
            loadTemplates();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete template');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading templates...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Header with Actions */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Settings Templates</h3>
                        <p className="text-sm text-gray-500 mt-1">
                            Save and reuse common settings configurations
                        </p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Template
                    </button>
                </div>

                {/* Filter */}
                <div className="flex items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700">Filter by role:</label>
                    <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="all">All Roles</option>
                        <option value="salesperson">Salesperson</option>
                        <option value="area_manager">Area Manager</option>
                        <option value="hq_manager">HQ Manager</option>
                    </select>
                </div>
            </div>

            {/* Success/Error Messages */}
            {success && (
                <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                    <Check className="w-5 h-5 text-green-600 mr-3" />
                    <p className="text-sm text-green-800">{success}</p>
                </div>
            )}

            {error && (
                <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                    <button
                        onClick={() => setError(null)}
                        className="text-red-400 hover:text-red-600 ml-4"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Templates Grid */}
            <div className="flex-1 overflow-y-auto p-6">
                {templates.length === 0 ? (
                    <div className="text-center py-12">
                        <Copy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No templates found</p>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                            Create your first template
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {templates.map(template => (
                            <TemplateCard
                                key={template.id}
                                template={template}
                                currentUser={currentUser}
                                selectedMembers={selectedMembers}
                                onApply={() => handleApplyTemplate(template)}
                                onDelete={() => handleDeleteTemplate(template.id)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Modals */}
            {showCreateModal && (
                <CreateTemplateModal
                    currentUser={currentUser}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        setSuccess('Template created successfully');
                        setTimeout(() => setSuccess(null), 3000);
                        loadTemplates();
                    }}
                />
            )}

            {showApplyModal && selectedTemplate && (
                <ApplyTemplateModal
                    template={selectedTemplate}
                    teamMembers={teamMembers}
                    preSelectedMembers={selectedMembers}
                    onClose={() => {
                        setShowApplyModal(false);
                        setSelectedTemplate(null);
                    }}
                    onSuccess={() => {
                        setShowApplyModal(false);
                        setSelectedTemplate(null);
                        setSuccess('Template applied successfully');
                        setTimeout(() => setSuccess(null), 3000);
                        if (onTemplateApplied) onTemplateApplied();
                    }}
                />
            )}
        </div>
    );
};

// ==========================================
// TEMPLATE CARD
// ==========================================

const TemplateCard = ({ template, currentUser, selectedMembers, onApply, onDelete }) => {
    const scopeColors = {
        organization: 'bg-blue-100 text-blue-800',
        area: 'bg-purple-100 text-purple-800',
        personal: 'bg-gray-100 text-gray-800'
    };

    const roleLabels = {
        salesperson: 'Salesperson',
        area_manager: 'Area Manager',
        hq_manager: 'HQ Manager'
    };

    const canDelete = template.created_by === currentUser.id || currentUser.role === 'admin';

    return (
        <div className={`border rounded-lg p-4 transition-all ${template.is_recommended
            ? 'border-blue-300 bg-blue-50'
            : 'border-gray-200 bg-white'
            } hover:shadow-md`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-semibold text-gray-900">{template.template_name}</h4>
                        {template.is_recommended && (
                            <Award className="w-4 h-4 text-blue-600" />
                        )}
                    </div>
                    {template.description && (
                        <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                    )}
                </div>
            </div>

            {/* Metadata */}
            <div className="flex items-center space-x-2 mb-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${scopeColors[template.scope]}`}>
                    {template.scope.charAt(0).toUpperCase() + template.scope.slice(1)}
                </span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {roleLabels[template.target_role]}
                </span>
            </div>

            {/* Tags */}
            {template.tags && template.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {template.tags.map(tag => (
                        <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs flex items-center">
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                        </span>
                    ))}
                </div>
            )}

            {/* Settings Preview */}
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <div className="text-xs font-semibold text-gray-700 mb-2">Settings ({Object.keys(template.settings_data).length}):</div>
                <div className="space-y-1">
                    {Object.entries(template.settings_data).slice(0, 3).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            <span className="font-medium text-gray-900">{value}</span>
                        </div>
                    ))}
                    {Object.keys(template.settings_data).length > 3 && (
                        <div className="text-xs text-gray-500 italic">
                            +{Object.keys(template.settings_data).length - 3} more...
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                    Applied {template.times_applied} time{template.times_applied !== 1 ? 's' : ''}
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={onApply}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm flex items-center"
                    >
                        Apply
                        <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                    {canDelete && (
                        <button
                            onClick={onDelete}
                            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// ==========================================
// CREATE TEMPLATE MODAL
// ==========================================

const CreateTemplateModal = ({ currentUser, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        template_name: '',
        description: '',
        scope: 'organization',
        target_role: 'salesperson',
        settings_data: {},
        tags: []
    });
    const [tagInput, setTagInput] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Available settings for each role (simplified - full list in backend)
    const availableSettings = {
        salesperson: [
            { key: 'morning_route_max_distance_km', label: 'Morning Route Max Distance (km)', defaultValue: 10.0 },
            { key: 'average_city_speed_kmh', label: 'Average City Speed (km/h)', defaultValue: 40 },
            { key: 'light_traffic_delay_factor', label: 'Light Traffic Delay Factor', defaultValue: 1.10 },
            { key: 'moderate_traffic_delay_factor', label: 'Moderate Traffic Delay Factor', defaultValue: 1.30 },
            { key: 'heavy_traffic_delay_factor', label: 'Heavy Traffic Delay Factor', defaultValue: 1.50 }
        ],
        area_manager: [
            { key: 'immediate_distance_max', label: 'Immediate Distance Max (km)', defaultValue: 10.0 },
            { key: 'near_distance_max', label: 'Near Distance Max (km)', defaultValue: 20.0 },
            { key: 'far_distance_max', label: 'Far Distance Max (km)', defaultValue: 30.0 }
        ],
        hq_manager: [
            { key: 'weight_priority', label: 'Priority Weight', defaultValue: 0.40 },
            { key: 'weight_revenue', label: 'Revenue Weight', defaultValue: 0.30 },
            { key: 'weight_proximity', label: 'Proximity Weight', defaultValue: 0.20 },
            { key: 'weight_opportunity', label: 'Opportunity Weight', defaultValue: 0.10 }
        ]
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData({
                ...formData,
                tags: [...formData.tags, tagInput.trim()]
            });
            setTagInput('');
        }
    };

    const handleRemoveTag = (tag) => {
        setFormData({
            ...formData,
            tags: formData.tags.filter(t => t !== tag)
        });
    };

    const handleSettingChange = (key, value) => {
        setFormData({
            ...formData,
            settings_data: {
                ...formData.settings_data,
                [key]: parseFloat(value) || value
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            await axios.post(`${API_BASE_URL}/api/manager/templates`, formData);
            onSuccess();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create template');
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900">Create New Template</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}

                    {/* Template Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Template Name *
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.template_name}
                            onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Urban Tokyo Profile"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Describe when to use this template..."
                        />
                    </div>

                    {/* Scope */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Scope *
                        </label>
                        <select
                            required
                            value={formData.scope}
                            onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="organization">Organization (All managers can use)</option>
                            <option value="area">Area (Area-specific)</option>
                            <option value="personal">Personal (Only you can use)</option>
                        </select>
                    </div>

                    {/* Target Role */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Target Role *
                        </label>
                        <select
                            required
                            value={formData.target_role}
                            onChange={(e) => setFormData({ ...formData, target_role: e.target.value, settings_data: {} })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="salesperson">Salesperson</option>
                            <option value="area_manager">Area Manager</option>
                            <option value="hq_manager">HQ Manager</option>
                        </select>
                    </div>

                    {/* Settings */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Settings *
                        </label>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            {availableSettings[formData.target_role].map(setting => (
                                <div key={setting.key} className="flex items-center justify-between">
                                    <label className="text-sm text-gray-700">{setting.label}</label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={formData.settings_data[setting.key] || ''}
                                        onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                                        placeholder={setting.defaultValue.toString()}
                                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Leave blank to not include in template</p>
                    </div>

                    {/* Tags */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tags
                        </label>
                        <div className="flex space-x-2 mb-2">
                            <input
                                type="text"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Add tags (press Enter)..."
                            />
                            <button
                                type="button"
                                onClick={handleAddTag}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                Add
                            </button>
                        </div>
                        {formData.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {formData.tags.map(tag => (
                                    <span key={tag} className="px-2 py-1 bg-blue-100 text-blue-800 rounded flex items-center text-sm">
                                        <Tag className="w-3 h-3 mr-1" />
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTag(tag)}
                                            className="ml-2 text-blue-600 hover:text-blue-800"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || Object.keys(formData.settings_data).length === 0}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Creating...' : 'Create Template'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// APPLY TEMPLATE MODAL
// ==========================================

const ApplyTemplateModal = ({ template, teamMembers, preSelectedMembers, onClose, onSuccess }) => {
    const [selectedUsers, setSelectedUsers] = useState(new Set(preSelectedMembers));
    const [applicationReason, setApplicationReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Filter team members by template's target role
    const eligibleMembers = teamMembers.filter(m => m.role === template.target_role);

    const toggleUser = (userId) => {
        const newSelected = new Set(selectedUsers);
        if (newSelected.has(userId)) {
            newSelected.delete(userId);
        } else {
            newSelected.add(userId);
        }
        setSelectedUsers(newSelected);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            await axios.post(
                `${API_BASE_URL}/api/manager/templates/${template.id}/apply`,
                {
                    user_ids: Array.from(selectedUsers),
                    application_reason: applicationReason
                }
            );
            onSuccess();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to apply template');
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900">Apply Template</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}

                    {/* Template Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="font-semibold text-blue-900">{template.template_name}</div>
                        <div className="text-sm text-blue-700 mt-1">
                            {Object.keys(template.settings_data).length} settings will be applied
                        </div>
                    </div>

                    {/* User Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Team Members ({selectedUsers.size} selected)
                        </label>
                        <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                            {eligibleMembers.length === 0 ? (
                                <div className="p-4 text-center text-gray-500">
                                    No eligible team members found
                                </div>
                            ) : (
                                eligibleMembers.map(member => (
                                    <label
                                        key={member.user_id}
                                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200 last:border-b-0"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.has(member.user_id)}
                                            onChange={() => toggleUser(member.user_id)}
                                            className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                        />
                                        <div className="ml-3 flex-1">
                                            <div className="text-sm font-medium text-gray-900">{member.name}</div>
                                            <div className="text-xs text-gray-500">{member.email}</div>
                                        </div>
                                        {member.has_customizations && (
                                            <span className="text-xs text-orange-600">
                                                Will override existing customizations
                                            </span>
                                        )}
                                    </label>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Application Reason */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reason for Applying (optional)
                        </label>
                        <textarea
                            value={applicationReason}
                            onChange={(e) => setApplicationReason(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., New hire onboarding, Performance improvement, etc."
                        />
                    </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || selectedUsers.size === 0}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Applying...' : `Apply to ${selectedUsers.size} user${selectedUsers.size !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TemplateManager;
